/**
 * @module
 * @format
 */

import {each, map, size, splice} from '@acng/frontend-bounty/std/array.js';
import {guard} from '@acng/frontend-rubicon';

/**
 * @import {Amateur} from "../amateurPool/model/amateur.js";
 * @import {FeedData, FeedItemType} from './types';
 */

import {cdn} from '../core/service/cdn.js';
import {hasFeature} from '../core/service/env.js';
import {checkPinById, promise as pinsAreAvailable} from '../pin/service/cache.js';
import {asFeedData, FEED_TYPE_WEBLOG, FEED_TYPE_MOVIE, FEED_TYPE_PICTURE} from './model.js';
import {FeedItem} from './item.js';

let cacheBust = '';

/**
 * @type {Readonly<Partial<Record<FeedItemType, boolean>>>}
 */
const availableFeedTypes = {
  [FEED_TYPE_WEBLOG]: true,
  [FEED_TYPE_MOVIE]: hasFeature('moviePool'),
  [FEED_TYPE_PICTURE]: hasFeature('picturePool'),
  // [FEED_TYPE_EVENT]: false,
  // [FEED_TYPE_PROFILE_IMAGE]: false,
};

/**
 * Remove entries of hidden features.
 *
 * @param {FeedData} feed
 * @returns {void}
 */
const removeHidden = (feed) => {
  for (let i = size(feed) - 1; i >= 0; i--) {
    const {type} = feed[i];
    const isAvailable = availableFeedTypes[type];

    if (!isAvailable) {
      splice(feed, i, 1);
    }
  }
};

// cacheBust!!!

/**
 * @param {Amateur["id"]} id
 * @param {FeedItemType | null} [filter]
 * @returns {Promise<FeedItem[]>}
 */
const fetchAmateurFeed = async (id, filter) => {
  const params = new URLSearchParams({
    //offset: `${offset}`,
    //limit: `${limit}`,
    type: filter ?? 'all',
    cacheBust,
  });

  const data = await cdn(`/api/amateur-profile/feed/${id}?${params}`);
  ASSERT: guard(data, asFeedData);
  removeHidden(data);

  return map(data, (itemData) => new FeedItem(itemData));
};

/**
 * @param {"all" | "posts" | "media" | "latest" | "favorites"} filter
 * @param {string} niche
 * @param {number} fsk
 * @param {number} offset - Not supported by AC IAPI.
 * @param {number} limit
 * @returns {Promise<FeedItem[]>}
 */
const fetchGlobalFeed = async (filter, niche, fsk, offset, limit) => {
  if (offset) {
    return [];
  }

  const latest = filter == 'latest';
  const favorites = filter == 'favorites';

  const params = new URLSearchParams({
    // offset: '0',
    limit: `${limit}`,
    filter:
      latest ? 'posts'
      : favorites ? 'all'
      : filter,
    latest: `${latest}`,
    niche,
    fsk: `${fsk}`,
  });

  const data = await cdn(`/api/amateur-profile/feed/global?${params}`);
  ASSERT: guard(data, asFeedData);
  removeHidden(data);

  if (latest) {
    each(data, (item) => (item.date = null));

    if (size(data)) {
      cacheBust = `${data[0].date}`;
    }
  }

  if (favorites) {
    await pinsAreAvailable;
  }

  return map(
    favorites ? data.filter((item) => checkPinById(item.user_id)) : data,
    (itemData) => new FeedItem(itemData),
  );
};

export {fetchAmateurFeed, fetchGlobalFeed};
